/**
 * useTravelMapGenerator — fills in missing map coordinates for a trip.
 *
 * Iterates every day and event on the trip that has no lat/lng yet, picks
 * the best-available text hint (accommodation address, city+country, event
 * notes, etc.), and calls the existing geocode endpoints for each.
 *
 * Uses the same Nominatim-backed pipeline as the auto-geocode-on-edit flow
 * in useTravelTrip, so results are deterministic: no AI guessing, no cost.
 * Rows with no usable text are skipped and reported back to the caller so
 * the UI can prompt the user to fill in a city or address.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import { queryKeys } from '@/lib/query-keys';
import type { TravelEventResponse, TravelItemResponse } from '../api';

export interface MapGeneratorResult {
  attempted: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

function itemQuery(item: TravelItemResponse): string | null {
  if (item.itemType !== 'day') return null;
  const addr = item.accommodationAddress?.trim();
  if (addr) return addr;
  const city = item.city?.trim();
  const country = item.country?.trim();
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  return null;
}

function eventQuery(
  event: TravelEventResponse,
  parentItem: TravelItemResponse | undefined,
): string | null {
  const parts: string[] = [];
  const title = event.title?.trim();
  if (title) parts.push(title);
  const notes = event.notes?.trim();
  if (notes && notes.length < 80) parts.push(notes);
  // Anchor with the day's city so "Louvre" resolves in Paris rather than Texas.
  if (parentItem?.itemType === 'day') {
    const city = parentItem.city?.trim();
    const country = parentItem.country?.trim();
    if (city && country) parts.push(`${city}, ${country}`);
    else if (city) parts.push(city);
  }
  if (parts.length === 0) return null;
  return parts.join(', ');
}

export function useTravelMapGenerator(tripId: string | undefined) {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePins = useCallback(
    async (
      items: TravelItemResponse[],
      events: TravelEventResponse[],
      options: { force?: boolean } = {},
    ): Promise<MapGeneratorResult> => {
      if (!tripId) {
        return { attempted: 0, succeeded: 0, failed: 0, skipped: 0 };
      }

      setIsGenerating(true);

      const itemsById = new Map(items.map((i) => [i.itemId, i]));
      const force = options.force === true;

      type Job =
        | { kind: 'item'; id: string; query: string }
        | { kind: 'event'; id: string; query: string };

      const jobs: Job[] = [];
      let skipped = 0;

      for (const item of items) {
        if (!force && item.lat != null && item.lng != null) continue;
        const q = itemQuery(item);
        if (!q) {
          if (item.itemType === 'day') skipped += 1;
          continue;
        }
        jobs.push({ kind: 'item', id: item.itemId, query: q });
      }

      for (const event of events) {
        if (!force && event.lat != null && event.lng != null) continue;
        const q = eventQuery(event, itemsById.get(event.itemId));
        if (!q) {
          skipped += 1;
          continue;
        }
        jobs.push({ kind: 'event', id: event.eventId, query: q });
      }

      let succeeded = 0;
      let failed = 0;

      // Sequential — Nominatim's public endpoint is 1 req/sec and the server
      // throttles us. Parallelizing would just generate 429s.
      for (const job of jobs) {
        try {
          if (job.kind === 'item') {
            await travelApi.geocodeTravelItem(job.id, job.query);
          } else {
            await travelApi.geocodeTravelEvent(job.id, job.query);
          }
          succeeded += 1;
        } catch {
          failed += 1;
        }
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.travel.detail(tripId) });

      setIsGenerating(false);
      return { attempted: jobs.length, succeeded, failed, skipped };
    },
    [tripId, queryClient],
  );

  return { generatePins, isGenerating };
}
